import {Bar, BarChart, Cell, LabelList, ResponsiveContainer, XAxis, YAxis} from 'recharts'
import {formatMinutesAsTimeString} from '../../../helpers/helperfunctions'
import {useTranslation} from 'react-i18next'
import {useEffect, useState} from "react"
import {cloneDeep} from "lodash"

const TOTAL_NUMBER_OF_MINUTES_PER_DAY = 1440

function getTotalTimeUsed(graphData, index, timerNum) {
    let total_time_used = 0
    for (let i = 0; i <= timerNum; i++) {
        total_time_used += graphData[index]["ws" + (i)] === undefined ? 0 : graphData[index]["ws" + (i)]
        total_time_used += graphData[index]["sh" + (i)] === undefined ? 0 : graphData[index]["sh" + (i)]
        total_time_used += graphData[index]["ws" + (i + 1)] === undefined ? 0 : graphData[index]["ws" + (i + 1)]
    }
    return total_time_used
}

function moveAllGraphsStepRight(graphData, nextDayIndex) {
    const numberOfGraphs = graphData[nextDayIndex].num_of_graphs
    for (let i = numberOfGraphs; i > 0; i--) {
        graphData[nextDayIndex]["ws" + (i + 1)] = graphData[nextDayIndex]["ws" + i]
        graphData[nextDayIndex]["sh" + (i + 1)] = graphData[nextDayIndex]["sh" + i]
        graphData[nextDayIndex]["sh" + (i + 1) + "_barcolor"] = graphData[nextDayIndex]["sh" + i + "_barcolor"]
        graphData[nextDayIndex]["sh" + (i + 1) + "_label_name"] = graphData[nextDayIndex]["sh" + i + "_label_name"]
        graphData[nextDayIndex]["sh" + (i + 1) + "_label_airflow"] = graphData[nextDayIndex]["sh" + i + "_label_airflow"]
    }
    graphData[nextDayIndex]["ws" + (1)] = "0"
    graphData[nextDayIndex]["sh" + (1)] = "0"
    graphData[nextDayIndex]["sh" + (1) + "_barcolor"] = ""
    graphData[nextDayIndex]["sh" + (1) + "_label_name"] = ""
    graphData[nextDayIndex]["sh" + (1) + "_label_airflow"] = ""
}

export default function TimeScheduleChart({scheduleList}) {
    const {t} = useTranslation(['settingspage', 'common'])

    let cleanGraphData = [
        {
            name: t('common:days.mon'),
            total_time_used: 0,
            num_of_graphs: 0
        },
        {
            name: t('common:days.tue'),
            total_time_used: 0,
            num_of_graphs: 0
        },
        {
            name: t('common:days.wed'),
            total_time_used: 0,
            num_of_graphs: 0
        },
        {
            name: t('common:days.thu'),
            total_time_used: 0,
            num_of_graphs: 0
        },
        {
            name: t('common:days.fri'),
            total_time_used: 0,
            num_of_graphs: 0
        },
        {
            name: t('common:days.sat'),
            total_time_used: 0,
            num_of_graphs: 0
        },
        {
            name: t('common:days.sun'),
            total_time_used: 0,
            num_of_graphs: 0
        }
    ];
    const [graphDataState, setGraphDataState] = useState(cleanGraphData)
  
    useEffect(() => {
        const graphData = cloneDeep(cleanGraphData);
        const scheduleListClone = cloneDeep(scheduleList);

        // Go through each schedule and if a schedule extends past midnight, create a secound schedule for the next day and set the duraction for the previous day to end at midnight.
        scheduleListClone.forEach((schedule, index) => {
            if (schedule.timerDuration + schedule.timerStartMinutesSinceMidnight > TOTAL_NUMBER_OF_MINUTES_PER_DAY) {
                const nextDaySchedule = cloneDeep(schedule);
                nextDaySchedule.timerStartMinutesSinceMidnight = 0;
                nextDaySchedule.timerDuration = (schedule.timerDuration + schedule.timerStartMinutesSinceMidnight) - TOTAL_NUMBER_OF_MINUTES_PER_DAY;
                // Shift dayBits one day forward (Mon→Tue, ..., Sun→Mon) so the overflow
                // is rendered on the day after the original schedule's day(s).
                nextDaySchedule.dayBits = schedule.dayBits[6] + schedule.dayBits.slice(0, 6);
                schedule.timerDuration = TOTAL_NUMBER_OF_MINUTES_PER_DAY - schedule.timerStartMinutesSinceMidnight;
                scheduleListClone.push(nextDaySchedule);
            }
        });

        // Sort schedules by start time and key for stable order
        const sortedSchedules = cloneDeep(scheduleListClone).sort((a, b) => {
            const timeDiff = a.timerStartMinutesSinceMidnight - b.timerStartMinutesSinceMidnight;
            if (timeDiff !== 0) return timeDiff;
            return a.key - b.key;
        });
        
        sortedSchedules.forEach(schedule => {
            if (!schedule.isEnabled) return;

            const {
                title,
                dayBits,
                mode,
                flowPercent,
                timerStartMinutesSinceMidnight,
                timerDuration,
                barColor
            } = schedule;

            const airflow = `${flowPercent}%`;

            for (let dayIdx = 0; dayIdx < 7; dayIdx++) {
                if (!getDayIsChecked(dayBits, dayIdx)) continue;

                let ignore = false;
                let totalTimeUsed = graphData[dayIdx].total_time_used;
                const graphIndex = graphData[dayIdx].num_of_graphs;
                let duration = timerDuration;
                const exceedsDay = (timerStartMinutesSinceMidnight + timerDuration) > TOTAL_NUMBER_OF_MINUTES_PER_DAY;

                // Handle schedules that exceed the day
                if (exceedsDay) {
                    duration = TOTAL_NUMBER_OF_MINUTES_PER_DAY - timerStartMinutesSinceMidnight;
                    const nextDayIdx = (dayIdx + 1) % 7;
                    const nextDayWhiteSpace = graphData[nextDayIdx]["ws1"] || 0;
                    const nextDayGraphs = graphData[nextDayIdx].num_of_graphs;
                    let nextDayDuration = (timerStartMinutesSinceMidnight + timerDuration) - TOTAL_NUMBER_OF_MINUTES_PER_DAY;

                    if (nextDayWhiteSpace === 0) {
                        graphData[nextDayIdx]["sh1"] = nextDayDuration;
                        graphData[nextDayIdx]["sh1_barcolor"] = barColor;
                        graphData[nextDayIdx]["sh1_label_name"] = title;
                        graphData[nextDayIdx]["sh1_label_airflow"] = mode !== undefined
                            ? t(`settingspage:device_timers.aware_mode_${mode}`)
                            : `${t('settingspage:device_timers.airflow')}:${airflow}`;
                        graphData[nextDayIdx].total_time_used = nextDayDuration;
                        graphData[nextDayIdx].num_of_graphs = 1;
                    } else {
                        if (nextDayWhiteSpace < nextDayDuration) {
                            nextDayDuration = nextDayWhiteSpace;
                        }
                        moveAllGraphsStepRight(graphData, nextDayIdx);
                        const newNextWhiteSpace = nextDayWhiteSpace - nextDayDuration;
                        graphData[nextDayIdx]["ws2"] = newNextWhiteSpace;
                        graphData[nextDayIdx]["ws1"] = 0;
                        graphData[nextDayIdx]["sh1"] = nextDayDuration;
                        graphData[nextDayIdx]["sh1_barcolor"] = barColor;
                        graphData[nextDayIdx]["sh1_label_name"] = title;
                        graphData[nextDayIdx]["sh1_label_airflow"] = mode !== undefined
                            ? t(`settingspage:device_timers.aware_mode_${mode}`)
                            : `${t('settingspage:device_timers.airflow')}:${airflow}`;
                        graphData[nextDayIdx].num_of_graphs = nextDayGraphs + 1;
                    }
                }

                // Handle whitespace and bar placement
                if (totalTimeUsed === 0) {
                    const wsMinutes = timerStartMinutesSinceMidnight === 0 ? 0 : timerStartMinutesSinceMidnight;
                    graphData[dayIdx][`ws${graphIndex + 1}`] = wsMinutes;
                    graphData[dayIdx][`sh${graphIndex + 1}`] = duration;
                    totalTimeUsed += wsMinutes + duration;
                } else if (totalTimeUsed <= timerStartMinutesSinceMidnight) {
                    const wsMinutes = timerStartMinutesSinceMidnight - totalTimeUsed;
                    graphData[dayIdx][`ws${graphIndex + 1}`] = wsMinutes;
                    totalTimeUsed += wsMinutes;
                    graphData[dayIdx][`sh${graphIndex + 1}`] = duration;
                    totalTimeUsed += duration;
                } else {
                    const previousTotalTimeUsed = getTotalTimeUsed(graphData, dayIdx, graphIndex - 1);
                    if (previousTotalTimeUsed <= timerStartMinutesSinceMidnight) {
                        const previousEndTime = previousTotalTimeUsed + graphData[dayIdx][`sh${graphIndex}`];
                        const currentEndTime = timerStartMinutesSinceMidnight + duration;
                        const setPreviousTimerDuration = timerStartMinutesSinceMidnight - previousTotalTimeUsed;
                        const previousBarTitle = graphData[dayIdx][`sh${graphIndex}_label_name`];
                        const previousBarAirFlow = graphData[dayIdx][`sh${graphIndex}_label_airflow`];
                        const previousBarColor = graphData[dayIdx][`sh${graphIndex}_barcolor`];

                        if (setPreviousTimerDuration <= 0) {
                            graphData[dayIdx][`sh${graphIndex}_label_name`] = "";
                            graphData[dayIdx][`sh${graphIndex}_label_airflow`] = "";
                        }

                        graphData[dayIdx][`sh${graphIndex}`] = setPreviousTimerDuration;
                        graphData[dayIdx][`sh${graphIndex + 1}_label_airflow`] = mode !== undefined
                            ? t(`settingspage:device_timers.aware_mode_${mode}`)
                            : `${t('settingspage:device_timers.airflow')}:${airflow}`;
                        graphData[dayIdx][`sh${graphIndex + 1}`] = duration;
                        totalTimeUsed = previousTotalTimeUsed + setPreviousTimerDuration + duration;

                        if (currentEndTime < previousEndTime) {
                            ignore = true;
                            graphData[dayIdx][`sh${graphIndex + 1}_barcolor`] = barColor;
                            graphData[dayIdx][`sh${graphIndex + 1}_label_name`] = title;
                            graphData[dayIdx][`sh${graphIndex + 1}_label_airflow`] = mode !== undefined
                                ? t(`settingspage:device_timers.aware_mode_${mode}`)
                                : `${t('settingspage:device_timers.airflow')}:${airflow}`;
                            const previousBarMissingDuration = previousEndTime - totalTimeUsed;
                            totalTimeUsed += previousBarMissingDuration;
                            graphData[dayIdx][`sh${graphIndex + 2}`] = previousBarMissingDuration;
                            graphData[dayIdx][`sh${graphIndex + 2}_barcolor`] = previousBarColor;
                            graphData[dayIdx][`sh${graphIndex + 2}_label_name`] = previousBarTitle;
                            graphData[dayIdx][`sh${graphIndex + 2}_label_airflow`] = previousBarAirFlow;
                            graphData[dayIdx].num_of_graphs = graphIndex + 2;
                        }
                    } else {
                        ignore = true;
                        console.error(`Time Scheduler Graph Error: dayIdx=${dayIdx} graphIndex=${graphIndex}`);
                    }
                }

                if (!ignore) {
                    graphData[dayIdx][`sh${graphIndex + 1}_barcolor`] = barColor;
                    graphData[dayIdx][`sh${graphIndex + 1}_label_name`] = title;
                    graphData[dayIdx][`sh${graphIndex + 1}_label_airflow`] = mode !== undefined
                        ? t(`settingspage:device_timers.aware_mode_${mode}`)
                        : `${t('settingspage:device_timers.airflow')}:${airflow}`;
                    graphData[dayIdx].num_of_graphs = graphIndex + 1;
                }

                graphData[dayIdx].total_time_used = totalTimeUsed;
            }
        });

        setGraphDataState(graphData);
    }, [scheduleList]);

    return (
        <ResponsiveContainer width="100%" height={300}>
            <BarChart
                width={1800}
                height={300}
                data={graphDataState}
                layout="vertical"
                margin={{top: 20, right: 30, left: 0, bottom: 0}}>

                <XAxis type="number" tickFormatter={(e) => formatMinutesAsTimeString(e)}
                       ticks={[0, 60, 120, 180, 240, 300, 360, 420, 480, 540, 600, 660, 720, 780, 840, 900, 960, 1020, 1080, 1140, 1200, 1260, 1320, 1380, 1439]}
                       domain={[0, 1439]}/>
                <YAxis type="category" dataKey="name"/>
                <Bar dataKey="ws1" fill="#ffffff" stackId="a" isAnimationActive={false}/>
                <Bar dataKey="sh1" stackId="a" isAnimationActive={false} radius={5}>
                    {
                        graphDataState.map((entry, index) => {
                            return <Cell fill={graphDataState[index].sh1_barcolor} key={`cell-${index}`}/>;
                        })
                    }
                    <LabelList style={{fontSize: '60%', fill: '#263d51'}} dataKey="sh1_label_name"
                               position="insideTop"/>
                    <LabelList style={{fontSize: '60%', fill: '#263d51'}} dataKey="sh1_label_airflow"
                               position="insideBottom"/>
                </Bar>
                <Bar dataKey="ws2" fill="#ffffff" stackId="a" isAnimationActive={false}/>
                <Bar dataKey="sh2" stackId="a" isAnimationActive={false} radius={5}>
                    {
                        graphDataState.map((entry, index) => {
                            return <Cell fill={graphDataState[index].sh2_barcolor} key={`cell-${index}`}/>;
                        })
                    }
                    <LabelList style={{fontSize: '60%', fill: '#263d51'}} dataKey="sh2_label_name"
                               position="insideTop"/>
                    <LabelList style={{fontSize: '60%', fill: '#263d51'}} dataKey="sh2_label_airflow"
                               position="insideBottom"/>
                </Bar>
                <Bar dataKey="ws3" fill="#ffffff" stackId="a" isAnimationActive={false}/>
                <Bar dataKey="sh3" stackId="a" isAnimationActive={false} radius={5}>
                    {
                        graphDataState.map((entry, index) => {
                            return <Cell fill={graphDataState[index].sh3_barcolor} key={`cell-${index}`}/>;
                        })
                    }
                    <LabelList style={{fontSize: '60%', fill: '#263d51'}} dataKey="sh3_label_name"
                               position="insideTop"/>
                    <LabelList style={{fontSize: '60%', fill: '#263d51'}} dataKey="sh3_label_airflow"
                               position="insideBottom"/>
                </Bar>
                <Bar dataKey="ws4" fill="#ffffff" stackId="a" isAnimationActive={false}/>
                <Bar dataKey="sh4" stackId="a" isAnimationActive={false} radius={5}>
                    {
                        graphDataState.map((entry, index) => {
                            return <Cell fill={graphDataState[index].sh4_barcolor} key={`cell-${index}`}/>;
                        })
                    }
                    <LabelList style={{fontSize: '60%', fill: '#263d51'}} dataKey="sh4_label_name"
                               position="insideTop"/>
                    <LabelList style={{fontSize: '60%', fill: '#263d51'}} dataKey="sh4_label_airflow"
                               position="insideBottom"/>
                </Bar>
                <Bar dataKey="ws5" fill="#ffffff" stackId="a" isAnimationActive={false}/>
                <Bar dataKey="sh5" stackId="a" isAnimationActive={false} radius={5}>
                    {
                        graphDataState.map((entry, index) => {
                            return <Cell fill={graphDataState[index].sh5_barcolor} key={`cell-${index}`}/>;
                        })
                    }
                    <LabelList style={{fontSize: '60%', fill: '#263d51'}} dataKey="sh5_label_name"
                               position="insideTop"/>
                    <LabelList style={{fontSize: '60%', fill: '#263d51'}} dataKey="sh5_label_airflow"
                               position="insideBottom"/>
                </Bar>
                <Bar dataKey="ws6" fill="#ffffff" stackId="a" isAnimationActive={false}/>
                <Bar dataKey="sh6" stackId="a" isAnimationActive={false} radius={5}>
                    {
                        graphDataState.map((entry, index) => {
                            return <Cell fill={graphDataState[index].sh6_barcolor} key={`cell-${index}`}/>;
                        })
                    }
                    <LabelList style={{fontSize: '60%', fill: '#263d51'}} dataKey="sh6_label_name"
                               position="insideTop"/>
                    <LabelList style={{fontSize: '60%', fill: '#263d51'}} dataKey="sh6_label_airflow"
                               position="insideBottom"/>
                </Bar>
                <Bar dataKey="ws7" fill="#ffffff" stackId="a" isAnimationActive={false}/>
                <Bar dataKey="sh7" stackId="a" isAnimationActive={false} radius={5}>
                    {
                        graphDataState.map((entry, index) => {
                            return <Cell fill={graphDataState[index].sh7_barcolor} key={`cell-${index}`}/>;
                        })
                    }
                    <LabelList style={{fontSize: '60%', fill: '#263d51'}} dataKey="sh7_label_name"
                               position="insideTop"/>
                    <LabelList style={{fontSize: '60%', fill: '#263d51'}} dataKey="sh7_label_airflow"
                               position="insideBottom"/>
                </Bar>
                <Bar dataKey="ws8" fill="#ffffff" stackId="a" isAnimationActive={false}/>
                <Bar dataKey="sh8" stackId="a" isAnimationActive={false} radius={5}>
                    {
                        graphDataState.map((entry, index) => {
                            return <Cell fill={graphDataState[index].sh8_barcolor} key={`cell-${index}`}/>;
                        })
                    }
                    <LabelList style={{fontSize: '60%', fill: '#263d51'}} dataKey="sh8_label_name"
                               position="insideTop"/>
                    <LabelList style={{fontSize: '60%', fill: '#263d51'}} dataKey="sh8_label_airflow"
                               position="insideBottom"/>
                </Bar>
                <Bar dataKey="ws8" fill="#ffffff" stackId="a" isAnimationActive={false}/>
                <Bar dataKey="sh9" stackId="a" isAnimationActive={false} radius={5}>
                    {
                        graphDataState.map((entry, index) => {
                            return <Cell fill={graphDataState[index].sh9_barcolor} key={`cell-${index}`}/>;
                        })
                    }
                    <LabelList style={{fontSize: '60%', fill: '#263d51'}} dataKey="sh9_label_name"
                               position="insideTop"/>
                    <LabelList style={{fontSize: '60%', fill: '#263d51'}} dataKey="sh9_label_airflow"
                               position="insideBottom"/>
                </Bar>
                <Bar dataKey="ws10" fill="#ffffff" stackId="a" isAnimationActive={false}/>
            </BarChart>
        </ResponsiveContainer>
    )
}

const getDayIsChecked = (dayBits, index) => dayBits[index] === '1'
